<template>
  <element>
    <v-simple-table dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th></th>
            <th
              v-for="(head, index) in headers"
              :key="index"
              class="table-head"
              @click="sortableColumns.includes(index) ? sort(index) : null"
            >
            <span>
              {{ head }}
              <element v-if="currentSort == index && sortableColumns.includes(index)">
                <v-icon v-if="sorted[index]" small>mdi-arrow-down</v-icon>
                <v-icon v-else small>mdi-arrow-up</v-icon>
              </element>
            </span>
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(feedback, index) in currentRows"
          >
          <tr class="no-border" :key="index * 4" v-if="expandedRows.includes(index)">
            <td class="thin-top-row" colspan="5"></td>
          </tr>
          <tr 
            :key="index * 4 + 1" 
            :class="{'expanded-row no-border': expandedRows.includes(index)}"
            @click="expandedRows.includes(index) ? collapseRow(index) : expandRow(index)">
            <td>
              <v-icon v-if="!expandedRows.includes(index)" small>mdi-chevron-right</v-icon>
              <v-icon v-else small>mdi-chevron-down</v-icon>    
            </td>
            <td class="user-name">
              <span class="d-block user-name abbr">{{ feedback.user_first_name }} {{ isMobile ? feedback.user_last_name.charAt(0).toUpperCase() + "." : feedback.user_last_name }}</span>
            </td>
            <td>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs}">
                  <span v-bind="attrs" v-on="on">{{ time2Elapsed(feedback.updated_at) }}</span>
                </template>
                <span>{{ feedback.updated_at }}</span>
              </v-tooltip>
            </td>
            <td class="task-name">
              <span class="abbr">{{ feedback.view_name }}</span>
            </td>
            <td>
              <v-row no-gutters justify="end">
                <v-col col="4" class="pr-2">
                  <v-icon v-if="feedback.has_document == 'true'" small color="#000">mdi-volume-high</v-icon>
                </v-col>
                <v-col col="4" class="pr-2">
                  <v-icon v-if="feedback.feedback_text.length !== 0" small color="#000">mdi-comment-text-outline</v-icon>
                </v-col>
                <v-col col="4" class="pr-2">
                  <v-icon v-if="feedback.sentiments == '1'" small color="#000">mdi-thumb-up</v-icon>
                  <v-icon v-else small color="#000">mdi-thumb-down</v-icon>
                </v-col>
              </v-row>
            </td>
          </tr>
          <tr v-if="expandedRows.includes(index)" :key="index * 4 + 2" class="no-border" rowspan="4">
            <td colspan="5">
              <div class="feedback-content h-100">
                <v-row no-gutters>
                  <v-col cols="8">
                    <h6 class="caption">{{ feedback.user_first_name }} {{ feedback.user_last_name }} / {{ feedback.view_name }}</h6>
                  </v-col>
                  <v-col cols="4" align="right">
                    <span class="timestamp">{{ formatDateTime(feedback.updated_at) }}</span>
                  </v-col>
                </v-row>
                <v-row no-gutters class="feedback-description">
                  <p class="mb-0">{{ feedback.feedback_text }}</p>
                </v-row>
                <v-spacer></v-spacer>
                <v-row no-gutters class="d-flex justify-content-end">
                  <v-btn 
                    class="audio-btn"
                    color="#033E8C" 
                    height=30 
                    :dark="feedback.has_document == 'true'"
                    :disabled="feedback.has_document != 'true'"
                    @click="playPauseAudio(feedback.feedback_id)"
                    >
                    <v-icon left small dark>mdi-play-pause</v-icon>
                    {{ $t('feedbackPage.expand.audioButton') }}
                  </v-btn>
                </v-row>
            </div>
            </td>
          </tr>
          <tr :key="index * 4 + 3" v-if="expandedRows.includes(index)">
            <td class="thin-bottom-row" colspan="5"></td>
          </tr>
          </template>
          <audio autoplay=false ref="audio" :src="audios[currentPlay]"></audio>
        </tbody>
      </template>
    </v-simple-table>
    <!-- pagination -->
    <v-row v-if="pageCount > 0" class="pagination" no-gutters justify="end" align="right" >
      <v-col cols="5" md="6" class="d-flex align-items-center justify-content-end mr-3">
        <span class="rows-per-page">{{ $t('common.rowsPerPage') }}</span>
      </v-col>
      <v-col cols="2" class="d-flex align-items-center justify-content-center mr-3">
        <v-select
          class="rows-select"
          :items="rowsPerPageItems"
          :value="rowsPerPage"
          :label="null"
          hide-details
          @change="changeRowsPerPage"
        ></v-select>
      </v-col>
      <v-col class="d-flex align-items-center justify-content-center">
        <v-btn :dark="currentPage > 1" fab x-small depressed color="#033E8C" :disabled="currentPage <= 1" width=24 height=24 @click="previousPage">
          <v-icon dark>mdi-chevron-left</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="2" md="1" class="d-flex align-items-center justify-content-center">
        <span>{{ currentPage }} / {{ pageCount }}</span>
      </v-col>
      <v-col class="d-flex align-items-center justify-content-center">
        <v-btn :dark="currentPage < pageCount" fab x-small depressed color="#033E8C" :disabled="currentPage >= pageCount" width=24 height=24 @click="nextPage">
          <v-icon dark>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-progress-circular v-if="isLoading" indeterminate color="#033e8c" class="text-center"></v-progress-circular>
  </element>
</template>

<script>
function compareDateTime(a, b) {
  const dateA = Date.parse(a.updated_at)
  const dateB = Date.parse(b.updated_at)

  if (dateA < dateB) {
    return -1
  }

  if (dateA > dateB) {
    return 1
  }

  return 0
}

function compareString(a, b, field1, field2) {
  let valueA
  let valueB

  if (!field2) {
    valueA = a[field1].toUpperCase()
    valueB = b[field1].toUpperCase()
  } else {
    valueA = a[field1].toUpperCase() + a[field2].toUpperCase()
    valueB = b[field1].toUpperCase() + b[field2].toUpperCase()
  }

  if (valueA < valueB) {
    return -1
  }
  
  if (valueA > valueB) {
    return 1
  }

  return 0
}

export default {
  name: "FeedbackTable",
  props: {
  },
  data() {
    return {
      feedbacks: [],
      isLoading: false,
      sorted: [false, false, false],
      currentSort: -1,
      currentPage: 1,
      rowsPerPage: 5,
      sortableColumns: [0, 1, 2],
      expandedRows: [],
      rowsPerPageItems: [5, 10, 15, 20, 25],
      audios: {},
      currentPlay: -1,
      document_type_id: 5,
    }
  },
  computed: {
    headers: function () {
      return this.$t('feedbackPage.table.headers')
    },
    currentRows: function () {
      if (this.feedbacks)
        return this.feedbacks.slice((this.currentPage - 1) * this.rowsPerPage, this.currentPage * this.rowsPerPage)
      else
        return []
    },
    pageCount: function () {
      if (this.feedbacks)
        return Math.ceil(this.feedbacks.length / this.rowsPerPage)
      else 
        return 0
    },
    isMobile: function () {
      return window.innerWidth <= 415
    }
  },
  watch: {
    rowsPerPage: function() {
      this.currentPage = 1
    }
  },
  methods: {
    time2Elapsed(deploymentTime) {
      const dd = Date.parse(deploymentTime)
      const now = Date.now()

      if (dd > now) {
        console.log("Invalid Datetime")
      }

      const diff = Math.abs(dd - now) // in millisseconds
      const diffDays = Math.floor(diff / 86400000);
      const diffHours = Math.floor((diff % 86400000) / 3600000);
      const diffMins = Math.round(((diff % 86400000) % 3600000) / 60000);

      let resultString = ''
      if (diffDays > 0) {
        resultString = this.$tc('dashboard.processTable.elapsed.day', diffDays, { count: diffDays })
      } else {
        if (diffHours > 0) {
          resultString = this.$tc('dashboard.processTable.elapsed.hour', diffHours, { count: diffHours })
        } else {
          if (diffMins > 0) {
            resultString = this.$tc('dashboard.processTable.elapsed.minute', diffMins, { count: diffMins })
          } else {
            resultString = this.$t('dashboard.processTable.elapsed.moment')
          }
        }
      }
      // console.log("%i Days %i Hours %i Minutes", diffDays, diffHours, diffMins)
      return resultString
    },
    formatDateTime(dtStr) {
      let dt = new Date(dtStr)
      let formatted = dt.getDate() + "/" + (dt.getMonth() + 1) + "/" + dt.getFullYear() + " " + dt.getHours() + ":" + dt.getMinutes()
      return formatted
    },
    sort(index) {
      this.expandedRows = []
      this.currentSort = index

      if (!this.$refs.audio.paused) {
        this.$refs.audio.pause()
      }
      this.sorted[index] = !this.sorted[index]
      
      if (index == 0) {
        this.feedbacks.sort(this.sorted[index] == false ? (a, b) => compareString(a, b, 'user_first_name', 'user_last_name') : (a, b) => -compareString(a, b, 'user_first_name', 'user_last_name'))
      } else if (index == 1) {
        this.feedbacks.sort(this.sorted[index] == false  ? (a, b) => compareDateTime(a, b) : (a, b) => -compareDateTime(a, b)) 
      } else if (index == 2) {
        this.feedbacks.sort(this.sorted[index] == false ? (a, b) => compareString(a, b, 'view_name') : (a, b) => -compareString(a, b, 'view_name'))
      } 
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
      if (this.expandedRows.length > 0)
        this.expandedRows = []
    },
    nextPage() {
      if (this.currentPage < this.pageCount) {
        this.currentPage++
      }
      if (this.expandedRows.length > 0)
        this.expandedRows = []
    },
    changeRowsPerPage(value) {
      this.rowsPerPage = value
    },
    expandRow(index) {
      this.expandedRows.push(index)
    },
    collapseRow(index) {
      for (var i = 0; i < this.expandedRows.length; i++) {
        if (this.expandedRows[i] === index) {
          this.expandedRows.splice(i, 1)
        }
      }

      if (this.currentRows[index].feedback_id == this.currentPlay) {
        this.$refs.audio.pause()
      }
    },
    convertBase64ToBlob(b64Data, contentType='', sliceSize=512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, {type: contentType});
      return blob;
    },
    playPauseAudio(id) {
      const audioPlayer = this.$refs.audio
      if (id in this.audios) { // audio was already fetched for the feedback
        if (id == this.currentPlay) {
          if (audioPlayer.paused) {
            audioPlayer.play()
          } else {
            audioPlayer.pause()
          }
        } else {
          this.currentPlay = id
          audioPlayer.play()
        }
      } else {
        const url = '/Documents/get?document_type_id=' + this.document_type_id + '&data_id=' + id
        this.axios.get(url, {}, { withCredentials: true })
        .then(response => {
          const data = response.data.data
          if (data && data.length > 0) {
            for (let i = 0; i < data.length; i++) {
              if (data[i].document_type_id == '5') {
                const blob = this.convertBase64ToBlob(data[i].data_doc_raw.split("data:audio/webm;base64,")[1]);
                this.audios[id] = window.URL.createObjectURL(blob)
                this.currentPlay = id
                const playPromise = audioPlayer.play()

                if (playPromise !== undefined) {
                  playPromise.then( () => {
                  })
                  .catch(error => {
                    console.log("Audio:", error)
                  })
                }
                break
              }
            }
          }
        })
        .catch(error => {
          console.log("Failed retrieving audio:", error)
        })
      }
    }
  },
  created() {
    this.isLoading = true
    this.axios.get('/Feedbacks/get', {}, { withCredentials: true })
    .then(response => {
      this.feedbacks = response.data.data
      this.sort(1)
    })
    .catch(error => {
      console.log(error)
    })
    .finally(() => {
      this.isLoading = false
    })
  }
}
</script>

<style scoped>
.table-head {
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: bold;
}

.rows-per-page {
  font-size: 0.875rem;
}

.thin-top-row {
  height: 2px!important;
}

.thin-bottom-row {
  height: 1px!important;
}
.expanded-row {
  background-color: #F2F2F2;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper)
{
  background: #f2f2f2;
}
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
  border-bottom: thin solid #f2f2f2;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row) {
  border-bottom: thin solid #f2f2f2;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr.no-border:not(:last-child) > td:not(.v-data-table__mobile-row) {
  border-bottom: none;
}

.feedback-content {
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  height: 8.25rem;
  width: 100%;
  background-color: #F2F2F2;
}

.feedback-description {
  font-size: 0.75rem;
  min-height: 3.25rem;
}

.pagination {
  border-radius: 0;
  border-bottom: thin solid #f2f2f2;
}

.rows-select >>> .v-select__selection--comma {
  font-size: 0.75rem;
  text-align: center!important;
}

.caption {
  font-size: 0.875rem;
  font-weight: bold;
}

.timestamp {
  font-size: 0.75rem;
  text-align: right;
}

.audio-btn {
  font-size: 0.75rem;
  font-weight: 600;
}

.v-text-field {
  padding-top: 0;
  margin-top: 0;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 0;
}

.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 0;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  color: black;
  font-size: 0.75rem;
}

.abbr {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media only screen and (max-width: 415px) {
  .user-name {
    width: 4.5rem;
  }

  .task-name {
    width: 7rem;
  }
}

@media only screen and (max-width: 376px) {
  .user-name {
    width: 4rem;
  }

  .task-name {
    width: 6.5rem;
  }
}

@media only screen and (max-width: 321px) {
  .user-name {
    width: 3rem;
  }

  .task-name {
    width: 5rem;
  }
}

</style>